type PreviewLinksParams = {
  pageUrl: string;
  experimentId: number;
  variantId: number;
  /**
   * The variant's report signature (`pertento-qa-sig`, PER-333), from
   * /api/editor-data/experiment/:id/preview-signatures: the QA preview then marks its reports as from
   * a signed link. Absent, the link is as before.
   */
  signature?: string | null;
};

/** An address typed or stored without a scheme ("shop.com/product"), which no link can be built from. */
export const withScheme = (url: string) => {
  const trimmed = url.trim();
  return !trimmed || /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Whole name parts (access_token, session_id, api-key), so keyword or zipcode stay.
const PRIVATE_PARAM =
  /(^|[_-])(token|key|secret|session|sid|auth|sig|signature|password|nonce|code)($|[_-])|preview|gclid|fbclid|msclkid|ttclid|^_ga$|^_gl$/i;

/**
 * Links that show a variant on the live site, for checking it on a phone or another
 * browser: the page with `pertento-force-variant=<experiment>-<variant>`, which the
 * visitor runtime remembers for that browser, and the same page with
 * `pertento-reset-variants=1` to go back to normal. The same link previews a running
 * experiment and one in QA (runtime-experiments/src/qa/). Used by the editor's preview
 * menu and the dashboard's QA panel.
 *
 * Parameters that are someone's own are dropped first, as the link is meant for sharing:
 * every `pertento…` one (the editor opens pages with its own, a sign-in token among them),
 * and the site's that look like credentials or tracking (tokens, keys, sessions, signed
 * links, theme previews, ad click ids). The page's other parameters stay, as they can
 * decide what the page shows. Null when the page address cannot be read.
 */
export const previewLinks = ({ pageUrl, experimentId, variantId, signature }: PreviewLinksParams) => {
  let base: URL;
  try {
    base = new URL(pageUrl);
  } catch {
    return null;
  }
  if (!/^https?:$/.test(base.protocol)) return null;
  for (const key of Array.from(base.searchParams.keys())) {
    if (key.toLowerCase().startsWith('pertento') || PRIVATE_PARAM.test(key)) base.searchParams.delete(key);
  }
  base.hash = '';

  const preview = new URL(base);
  preview.searchParams.set('pertento-force-variant', `${experimentId}-${variantId}`);
  if (signature) preview.searchParams.set('pertento-qa-sig', signature);
  const reset = new URL(base);
  reset.searchParams.set('pertento-reset-variants', '1');
  return { preview: preview.toString(), reset: reset.toString() };
};
